import { useCallback, useState } from "react";

import { ApolloError } from "@apollo/client";

import { getLatestDialogue } from "@/features/socratic/helpers/get-latest-dialogue";
import {
  CreateSocraticDialogueError,
  SocraticDialogueFragment,
  SocraticSessionFragment,
  useCreateSocraticDialogueMutation,
} from "@/generated/graphql";

export type CreateDialogueControls = ReturnType<typeof useCreateDialogue>;

// `"UNKNOWN"` stands in for a network or GraphQL failure, which carries no
// error value of its own.
export type CreateDialogueError = CreateSocraticDialogueError | "UNKNOWN";

export type CreateMetadata = {
  attemptNumber: {
    prev: number | null;
    next: number;
  };
};

export function useCreateDialogue() {
  const [mutation, { loading, data, error }] =
    useCreateSocraticDialogueMutation();

  const [createMetadata, setCreateMetadata] = useState<CreateMetadata | null>(
    null
  );

  const createDialogue = useCallback(
    ({
      workId,
      session,
      onSuccess,
    }: {
      workId: string;
      session: SocraticSessionFragment;
      onSuccess?: ({
        updatedSession,
        newDialogue,
      }: {
        updatedSession: SocraticSessionFragment;
        newDialogue: SocraticDialogueFragment;
      }) => void;
    }) => {
      const { id: sessionId, dialogues } = session;
      const latestDialogue = getLatestDialogue(dialogues);

      const newAttemptNumber = latestDialogue
        ? latestDialogue.attemptNumber + 1
        : 1;

      setCreateMetadata({
        attemptNumber: {
          prev: latestDialogue?.attemptNumber ?? null,
          next: newAttemptNumber,
        },
      });

      return mutation({
        variables: {
          workId,
          sessionId,
          attemptNumber: newAttemptNumber,
        },
        onCompleted: (mutationData) => {
          const updatedSession = mutationData.createSocraticDialogue.session;
          const newDialogue = updatedSession
            ? getLatestDialogue(updatedSession.dialogues)
            : null;
          if (updatedSession && newDialogue) {
            onSuccess?.({ updatedSession, newDialogue });
          }
        },
      });
    },
    [mutation]
  );

  return {
    createDialogue,
    creatingDialogue: loading,
    createError: createDialogueError(error, data?.createSocraticDialogue.error),
    createMetadata,
  };
}

function createDialogueError(
  error: ApolloError | undefined,
  payloadError: CreateSocraticDialogueError | null | undefined
): CreateDialogueError | null {
  if (error !== undefined) return "UNKNOWN";
  return payloadError ?? null;
}
